import { useEffect, useMemo, useRef, useState } from "react";
import { ExternalLink, FileJson, FileSpreadsheet, Search, ShieldAlert, X } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { AggregatedAppCveResult } from "@/services/appCveMatcher";
import { NvdCveData } from "@/services/nvdApi";
import { buildPerVersionEntries, sortPerVersionEntries, type PerVersionSortMode } from "./perVersionSort";

interface AppCveDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appName: string;
  bundleId: string;
  /** All detected versions of this app, used for context in the header. */
  versions: string[];
  match: AggregatedAppCveResult | undefined;
  onRemediate?: () => void;
  remediationLabel?: string;
  remediationDescription?: string;
}

const SEVERITY_STYLES: Record<NvdCveData["severity"], string> = {
  CRITICAL: "bg-destructive text-destructive-foreground",
  HIGH: "bg-destructive/80 text-destructive-foreground",
  MEDIUM: "bg-warning text-warning-foreground",
  LOW: "bg-muted text-muted-foreground",
  NONE: "bg-muted text-muted-foreground",
};

const versionDomId = (version: string) =>
  `pv-row-${version.replace(/[^a-zA-Z0-9._-]/g, "_") || "unknown"}`;

export function AppCveDetailSheet({
  open,
  onOpenChange,
  appName,
  bundleId,
  versions,
  match,
  onRemediate,
  remediationLabel = "Remediate via deployment",
  remediationDescription,
}: AppCveDetailSheetProps) {
  const cves = match?.cves ?? [];
  const sorted = [...cves].sort((a, b) => (b.cvssScore ?? 0) - (a.cvssScore ?? 0));
  const hasKeywordMatch = match?.perVersion.some((r) => r.source === "keyword") ?? false;
  const vulnerableVersions = match?.vulnerableVersions ?? [];
  const versionLabel = versions.length === 1 ? versions[0] : `${versions.length} versions`;

  // Reverse index: cveId -> versions that produced it.
  const cveToVersions = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!match) return map;
    for (const r of match.perVersion) {
      const v = (r.version || "Unknown").trim() || "Unknown";
      for (const c of r.cves) {
        const arr = map.get(c.cveId) ?? [];
        if (!arr.includes(v)) arr.push(v);
        map.set(c.cveId, arr);
      }
    }
    return map;
  }, [match]);

  const [highlighted, setHighlighted] = useState<string | null>(null);
  const [sortMode, setSortMode] = useState<PerVersionSortMode>("severity");
  const [aggFilter, setAggFilter] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const clearTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (clearTimer.current) window.clearTimeout(clearTimer.current);
    };
  }, []);

  const jumpToVersion = (version: string) => {
    const id = versionDomId(version);
    const el = scrollRef.current?.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setHighlighted(version);
    if (clearTimer.current) window.clearTimeout(clearTimer.current);
    clearTimer.current = window.setTimeout(() => setHighlighted(null), 2000);
  };

  const filterQuery = aggFilter.trim().toLowerCase();
  const matchesFilter = (version: string, cves: { cveId: string }[]) => {
    if (!filterQuery) return true;
    if (version.toLowerCase().includes(filterQuery)) return true;
    return cves.some((c) => c.cveId.toLowerCase().includes(filterQuery));
  };
  const filteredVulnerableVersions = vulnerableVersions
    .map((v, idx) => ({ v, idx }))
    .filter(({ v }) => {
      const row = match?.perVersion.find((r) => (r.version ?? "").trim() === v);
      return matchesFilter(v, row?.cves ?? []);
    });
  const excludedVersions = match
    ? match.versionsEvaluated.filter((v) => !vulnerableVersions.includes(v))
    : [];
  const filteredExcludedVersions = excludedVersions.filter((v) => matchesFilter(v, []));
  const filteredPerVersion = match
    ? sortPerVersionEntries(buildPerVersionEntries(match.perVersion), sortMode).filter(({ r }) =>
        matchesFilter((r.version || "Unknown").trim() || "Unknown", r.cves),
      )
    : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-hidden flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-destructive" />
            {appName}
          </SheetTitle>
          <SheetDescription>
            <span className="font-mono text-xs">{bundleId}</span> · {versionLabel}
            {hasKeywordMatch && (
              <span className="ml-2 text-xs italic">(includes keyword matches — verify before remediation)</span>
            )}
            {vulnerableVersions.length > 0 && (
              <div className="mt-1 text-xs">
                Vulnerable versions:{" "}
                <span className="font-mono">{vulnerableVersions.join(", ")}</span>
              </div>
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-2 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              {sorted.length} CVE{sorted.length === 1 ? "" : "s"} matched
            </div>
            {onRemediate && (
              <Button size="sm" onClick={onRemediate}>
                {remediationLabel}
              </Button>
            )}
          </div>
          {onRemediate && remediationDescription && (
            <p className="text-xs text-muted-foreground">{remediationDescription}</p>
          )}
        </div>
        <Separator />

        <ScrollArea className="flex-1 -mx-6 px-6">
          <div ref={scrollRef} className="space-y-4 py-4">
            {match && (
              <div className="rounded-lg border border-dashed p-3 space-y-2 bg-muted/30">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Aggregation source — vulnerableVersions
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Badge variant="outline" className="text-[10px]">
                      {vulnerableVersions.length} of {match.versionsEvaluated.length} evaluated
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-[10px]"
                      onClick={() => exportAggregation("csv", appName, bundleId, match)}
                    >
                      <FileSpreadsheet className="h-3 w-3 mr-1" />
                      CSV
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-[10px]"
                      onClick={() => exportAggregation("json", appName, bundleId, match)}
                    >
                      <FileJson className="h-3 w-3 mr-1" />
                      JSON
                    </Button>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Each entry below is one element of the <span className="font-mono">vulnerableVersions</span> array
                  with the per-version CVE count that qualified it for inclusion.
                </p>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    type="search"
                    value={aggFilter}
                    onChange={(e) => setAggFilter(e.target.value)}
                    placeholder="Filter by version or CVE id (e.g. 1.2.3 or CVE-2024-)"
                    className="h-7 pl-7 pr-7 text-xs"
                    aria-label="Filter vulnerable versions"
                  />
                  {aggFilter && (
                    <button
                      type="button"
                      onClick={() => setAggFilter("")}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label="Clear filter"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                {vulnerableVersions.length === 0 ? (
                  <div className="text-xs text-muted-foreground italic">
                    No versions qualified — array is empty.
                  </div>
                ) : filteredVulnerableVersions.length === 0 ? (
                  <div className="text-xs text-muted-foreground italic">
                    No vulnerable versions match "{aggFilter}".
                  </div>
                ) : (
                  <ol className="space-y-1 text-xs">
                    {filteredVulnerableVersions.map(({ v, idx }) => {
                      const row = match.perVersion.find(
                        (r) => (r.version ?? "").trim() === v,
                      );
                      const count = row?.cves.length ?? 0;
                      return (
                        <li
                          key={`${v}-${idx}`}
                          className="flex items-center justify-between gap-2 font-mono"
                        >
                          <span>
                            <span className="text-muted-foreground">[{idx}]</span> {v}
                          </span>
                          <span className="text-muted-foreground">
                            {count} CVE{count === 1 ? "" : "s"}
                            {row?.source === "keyword" && (
                              <span className="ml-1 italic">· keyword</span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                )}
                {excludedVersions.length > 0 && (
                  <details className="text-[11px] text-muted-foreground">
                    <summary className="cursor-pointer hover:text-foreground">
                      Excluded versions ({filterQuery ? `${filteredExcludedVersions.length} of ` : ""}{excludedVersions.length})
                    </summary>
                    <ul className="mt-1 space-y-0.5 font-mono">
                      {filteredExcludedVersions.length === 0 ? (
                        <li className="italic">No excluded versions match "{aggFilter}".</li>
                      ) : (
                        filteredExcludedVersions.map((v) => <li key={v}>{v} — 0 CVEs</li>)
                      )}
                    </ul>
                  </details>
                )}
              </div>
            )}
            {match && match.perVersion.some((r) => r.cves.length > 0) && (
              <div className="rounded-lg border p-3 space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Per-version breakdown
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      role="group"
                      aria-label="Sort versions"
                      className="inline-flex rounded-md border bg-muted/30 p-0.5"
                    >
                      <button
                        type="button"
                        onClick={() => setSortMode("severity")}
                        aria-pressed={sortMode === "severity"}
                        className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
                          sortMode === "severity"
                            ? "bg-background text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        Severity
                      </button>
                      <button
                        type="button"
                        onClick={() => setSortMode("total")}
                        aria-pressed={sortMode === "total"}
                        className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
                          sortMode === "total"
                            ? "bg-background text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        Total CVEs
                      </button>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                      <span className="w-8 text-center">Crit</span>
                      <span className="w-8 text-center">High</span>
                      <span className="w-8 text-center">Med</span>
                      <span className="w-8 text-center">Low</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {filteredPerVersion.length === 0 ? (
                    <div className="text-xs text-muted-foreground italic px-2 py-1">
                      No versions match "{aggFilter}".
                    </div>
                  ) : (
                    filteredPerVersion.map(({ r, counts }) => {
                      const versionKey = (r.version || "Unknown").trim() || "Unknown";
                      const isHighlighted = highlighted === versionKey;
                      return (
                        <div
                          key={r.version}
                          id={versionDomId(versionKey)}
                          className={`flex items-center justify-between gap-2 text-xs rounded-md px-2 py-1 transition-colors ${
                            isHighlighted ? "bg-primary/10 ring-1 ring-primary/40" : ""
                          }`}
                        >
                          <span className="font-mono truncate">{r.version || "Unknown"}</span>
                          <div className="flex items-center gap-2">
                            {r.source === "keyword" && (
                              <span className="italic text-[10px] text-muted-foreground">keyword</span>
                            )}
                            <div
                              className="flex items-center gap-2 tabular-nums"
                              aria-label={`Critical ${counts.CRITICAL}, High ${counts.HIGH}, Medium ${counts.MEDIUM}, Low ${counts.LOW}`}
                            >
                              <SeverityCount label="Crit" value={counts.CRITICAL} tone="critical" />
                              <SeverityCount label="High" value={counts.HIGH} tone="high" />
                              <SeverityCount label="Med" value={counts.MEDIUM} tone="medium" />
                              <SeverityCount label="Low" value={counts.LOW} tone="low" />
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
            {sorted.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-8">
                No CVE details available.
              </div>
            )}
            {sorted.map((cve) => {
              const triggeredBy = cveToVersions.get(cve.cveId) ?? [];
              return (
                <div key={cve.cveId} className="rounded-lg border p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <a
                      href={`https://nvd.nist.gov/vuln/detail/${cve.cveId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-sm font-medium hover:underline flex items-center gap-1"
                    >
                      {cve.cveId}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                    <div className="flex items-center gap-2">
                      {cve.cvssScore != null && (
                        <span className="text-xs text-muted-foreground">
                          CVSS {cve.cvssScore.toFixed(1)}
                        </span>
                      )}
                      <Badge className={SEVERITY_STYLES[cve.severity]}>{cve.severity}</Badge>
                    </div>
                  </div>
                  {cve.description && (
                    <p className="text-xs text-muted-foreground leading-relaxed line-clamp-4">
                      {cve.description}
                    </p>
                  )}
                  {triggeredBy.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap pt-1">
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        Triggered by
                      </span>
                      {triggeredBy.map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => jumpToVersion(v)}
                          className="font-mono text-[10px] px-1.5 py-0.5 rounded border border-border bg-muted/50 hover:bg-primary/10 hover:border-primary/40 hover:text-primary transition-colors"
                          title={`Jump to version ${v} in the per-version breakdown`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  )}
                  {cve.publishedDate && (
                    <p className="text-[10px] text-muted-foreground">
                      Published {new Date(cve.publishedDate).toLocaleDateString()}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

function SeverityCount({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "critical" | "high" | "medium" | "low";
}) {
  const isZero = value === 0;
  const toneClass = isZero
    ? "text-muted-foreground/50 border-border/50"
    : tone === "critical"
      ? "text-destructive border-destructive/40 bg-destructive/10"
      : tone === "high"
        ? "text-destructive border-destructive/30 bg-destructive/5"
        : tone === "medium"
          ? "text-warning border-warning/40 bg-warning/10"
          : "text-muted-foreground border-border bg-muted/40";
  return (
    <span
      title={`${label}: ${value}`}
      className={`inline-flex items-center justify-center w-8 h-5 rounded border text-[10px] font-medium ${toneClass}`}
    >
      {value}
    </span>
  );
}

function downloadFile(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function csvEscape(value: string | number): string {
  const s = String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function exportAggregation(
  format: "csv" | "json",
  appName: string,
  bundleId: string,
  match: AggregatedAppCveResult,
) {
  const stamp = new Date().toISOString().slice(0, 10);
  const safeBundle = bundleId.replace(/[^a-zA-Z0-9._-]/g, "_");
  const baseName = `vulnerable-versions-${safeBundle}-${stamp}`;

  const vulnerableSet = new Set(match.vulnerableVersions);
  const rows = match.versionsEvaluated.map((version) => {
    const row = match.perVersion.find((r) => (r.version ?? "").trim() === version);
    const cves = row?.cves ?? [];
    const counts: Record<NvdCveData["severity"], number> = {
      CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0, NONE: 0,
    };
    cves.forEach((c) => { counts[c.severity]++; });
    return {
      app: appName,
      bundleId,
      version,
      included: vulnerableSet.has(version),
      cveCount: cves.length,
      critical: counts.CRITICAL,
      high: counts.HIGH,
      medium: counts.MEDIUM,
      low: counts.LOW,
      source: row?.source ?? "none",
      cveIds: cves.map((c) => c.cveId),
    };
  });

  if (format === "json") {
    const payload = {
      app: appName,
      bundleId,
      generatedAt: new Date().toISOString(),
      summary: {
        versionsEvaluated: match.versionsEvaluated,
        vulnerableVersions: match.vulnerableVersions,
        excludedVersions: match.versionsEvaluated.filter((v) => !vulnerableSet.has(v)),
        totalCves: match.cves.length,
        highestSeverity: match.highestSeverity,
      },
      versions: rows,
    };
    downloadFile(`${baseName}.json`, JSON.stringify(payload, null, 2), "application/json");
    toast.success("Aggregation exported as JSON");
    return;
  }

  const headers = [
    "App", "Bundle ID", "Version", "Included in vulnerableVersions",
    "CVE Count", "Critical", "High", "Medium", "Low", "Source", "CVE IDs",
  ];
  const lines = [headers.map(csvEscape).join(",")];
  for (const r of rows) {
    lines.push(
      [
        r.app, r.bundleId, r.version, r.included ? "yes" : "no",
        r.cveCount, r.critical, r.high, r.medium, r.low, r.source,
        r.cveIds.join("; "),
      ].map(csvEscape).join(","),
    );
  }
  downloadFile(`${baseName}.csv`, lines.join("\n"), "text/csv");
  toast.success("Aggregation exported as CSV");
}
